import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { useRegion } from '@/context/RegionContext';
import { useAuth } from '@/context/AuthContext';
import { getSiteSettings } from '@/lib/siteSettings';


const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const location = useLocation();
  const { getCartCount } = useCart();
  const { regionKey, regions, openRegionPicker } = useRegion();
  const { user, login } = useAuth();
  const cartCount = getCartCount();
  const currentRegion = regions[regionKey];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    getSiteSettings().then(res => {
      if (res.data?.logo_image_url) setLogoUrl(res.data.logo_image_url);
    }).catch(() => {});
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { label: 'Gallery', path: '/gallery', children: [
      { path: '/gallery', label: 'All Artworks' },
      { path: '/gallery?art_type=Traditional', label: 'Traditional' },
      { path: '/gallery?art_type=Digital', label: 'Digital' },
      { type: 'divider', label: 'Portraits' },
      { path: '/portraits/person', label: 'Person' },
      { path: '/portraits/family', label: 'Family' },
      { path: '/portraits/pet', label: 'Pet' },
      { type: 'divider' },
      { path: '/collections', label: 'Collections' },
      { path: '/commissions', label: 'Custom Paintings' },
    ]},
    { path: '/exhibitions', label: 'Exhibitions' },
    { path: '/testimonials', label: 'Reviews' },
    { label: 'Blog & Press', path: '/blog', children: [
      { path: '/blog', label: 'Blog' },
      { path: '/press', label: 'Press' },
    ]},
    { path: '/track-order', label: 'Track Order' },
    { path: '/contact', label: 'Contact' },
  ];

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeoutRef = useRef(null);

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'backdrop-blur-xl bg-background/85 shadow-sm'
          : 'backdrop-blur-md bg-background/60' /* frosted, so menu text stays readable over busy hero images */
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="transition-opacity hover:opacity-70"
            data-testid="logo-link"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Gangadhar Som" className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto object-contain" data-testid="logo-image" />
            ) : (
              <span className="font-serif text-xl sm:text-2xl md:text-3xl font-light tracking-tight">Gangadhar Som</span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3 lg:space-x-5">
            {navLinks.map((link) => (
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => {
                    clearTimeout(dropdownTimeoutRef.current);
                    setOpenDropdown(link.label);
                  }}
                  onMouseLeave={() => {
                    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
                  }}
                >
                  <button
                    data-testid={`nav-dropdown-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`relative font-sans uppercase tracking-[0.12em] text-[10px] lg:text-[11px] font-medium transition-colors flex items-center gap-0.5 whitespace-nowrap ${
                      link.children.some(c => c.path && (location.pathname === c.path || location.pathname + location.search === c.path)) ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={10} className={`transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openDropdown === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 bg-background/95 backdrop-blur-xl border border-border rounded-lg shadow-lg py-1.5 min-w-[180px] z-50"
                      >
                        {link.children.map((child, idx) =>
                          child.type === 'divider' ? (
                            <div key={`divider-${idx}`} className="border-t border-border/50 my-1 mx-3">
                              {child.label && (
                                <span className="block px-1 pt-1.5 pb-0.5 text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground/60">{child.label}</span>
                              )}
                            </div>
                          ) : (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`nav-link-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => setOpenDropdown(null)}
                            className={`block px-4 py-2 font-sans text-[11px] uppercase tracking-[0.1em] font-medium transition-colors ${
                              (location.pathname + location.search) === child.path || location.pathname === child.path ? 'text-accent bg-accent/5' : 'text-muted-foreground hover:text-foreground hover:bg-accent/5'
                            }`}
                          >
                            {child.label}
                          </Link>
                          )
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative font-sans uppercase tracking-[0.12em] text-[10px] lg:text-[11px] font-medium transition-colors link-underline whitespace-nowrap ${
                  location.pathname === link.path ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
              )
            ))}
            <Link
              to="/cart" data-testid="nav-cart-link"
              className="relative p-2 transition-colors hover:text-accent"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 bg-accent text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                >
                  {cartCount}
                </motion.span>
              )}
            </Link>
            <button
              onClick={openRegionPicker}
              data-testid="region-toggle"
              className="px-3 py-1.5 text-xs font-semibold rounded-full border-2 border-accent/30 hover:border-accent bg-accent/5 hover:bg-accent/10 transition-all flex items-center gap-1.5"
              title="Change region"
            >
              <span className="text-sm">{currentRegion?.flag}</span>
              <span className="font-bold">{currentRegion?.label}</span>
              <span className="text-muted-foreground">({currentRegion?.currencySymbol})</span>
            </button>
            {user ? (
              <Link to="/profile" data-testid="nav-profile-link" className="relative p-1 transition-colors hover:text-accent" title="My Account">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-border" />
                ) : (
                  <User size={20} />
                )}
              </Link>
            ) : (
              <button onClick={login} data-testid="nav-login-btn" className="font-sans uppercase tracking-[0.15em] text-xs font-medium text-muted-foreground hover:text-accent transition-colors">
                Sign In
              </button>
            )}
          </div>

          {/* Mobile: Cart + Currency + Hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            {user ? (
              <Link to="/profile" data-testid="mobile-profile-link" className="p-1">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full border border-border" />
                ) : (
                  <User size={18} />
                )}
              </Link>
            ) : (
              <button onClick={login} data-testid="mobile-login-btn" className="text-[10px] font-sans uppercase tracking-widest font-medium text-muted-foreground">
                Sign In
              </button>
            )}
            <button
              onClick={openRegionPicker}
              data-testid="mobile-region-toggle"
              className="px-2 py-1 text-[11px] font-bold rounded-full border-2 border-accent/30 bg-accent/5 flex items-center gap-1"
            >
              <span>{currentRegion?.flag}</span>
              <span>{currentRegion?.label}</span>
            </button>
            <Link to="/cart" data-testid="mobile-cart-link" className="relative p-2">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span data-testid="mobile-cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button data-testid="mobile-menu-button" className="p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Full screen overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-background/98 backdrop-blur-xl z-40"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-1 flex flex-col">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  {link.children ? (
                    <>
                      <span className="block py-3 font-sans uppercase tracking-[0.2em] text-sm font-medium text-muted-foreground border-b border-border/50">
                        {link.label}
                      </span>
                      {link.children.map((child, idx) =>
                        child.type === 'divider' ? (
                          <div key={`m-divider-${idx}`} className="border-t border-border/30 my-1 ml-4">
                            {child.label && (
                              <span className="block px-0 pt-1 pb-0.5 text-[9px] uppercase tracking-[0.15em] font-semibold text-muted-foreground/50">{child.label}</span>
                            )}
                          </div>
                        ) : (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          data-testid={`mobile-nav-link-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`block py-2.5 pl-4 font-sans uppercase tracking-[0.15em] text-xs font-medium transition-colors border-b border-border/30 ${
                            (location.pathname + location.search) === child.path || location.pathname === child.path ? 'text-accent' : 'text-foreground'
                          }`}
                        >
                          {child.label}
                        </Link>
                        )
                      )}
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`block py-3 font-sans uppercase tracking-[0.2em] text-sm font-medium transition-colors border-b border-border/50 ${
                        location.pathname === link.path ? 'text-accent' : 'text-foreground'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
